<script lang="ts" setup>
import GenderBadge from "@/components/MateStyle/GenderBadge.vue";
import UserAvatar from "@/components/UserAvatar.vue";
import AdmireIcon from "@/components/icon/AdmireIcon.vue";
import FollowIcon from "@/components/icon/FollowIcon.vue";
import RequestIcon from "@/components/icon/RequestIcon.vue";
import ChatIcon from "@/components/icon/chatIcon.vue";
import { startChatSession } from "@/components/mobile/chat/chatUtils";
import i18n from "@/locales";
import { useAdmire } from "@/stores/admire";
import { useFollow } from "@/stores/follow";
import { useMate } from "@/stores/mate";
import { Admirer, Mate } from "@/types/entity";
import { useMateTypeText } from "@/use/common";
import { showFailToast, showSuccessToast, TextEllipsis } from "vant";
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

/**
 * 未关注：展示关注按钮 - 关注高亮
 * 已关注：展示关注中、守护按钮 - 守护高亮
 * 已守护：展示守护中、未Ta创作按钮 - 创作高亮
 */
// Props & Emit
export interface MateCardProps {
  showChatBtn?: boolean;
  mate: Mate;
  enableBtnGroup?: boolean;
}

const props: MateCardProps = withDefaults(defineProps<MateCardProps>(), {
  showChatBtn: true,
  enableBtnGroup: true,
});

// 状态
const mateStore = useMate();
const router = useRouter();
const gradientStyle = "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))";
const mateType = useMateTypeText(props.mate.type);
const bannerStyle = computed(() => {
  if (props.mate.bannerUrl) {
    return {
      background: `${gradientStyle}, url(${props.mate.bannerUrl}) no-repeat`,
      backgroundSize: "cover",
    };
  } else if (props.mate.mediaUrl) {
    return {
      filter: "blur(5px)",
      background: `${gradientStyle}, url(${props.mate.mediaUrl})`,
      backgroundSize: "cover",
    };
  } else {
    return {
      filter: "blur(3px)",
      backgroundImage:
        "linear-gradient(45deg, rgb(81 7 115) 0%, rgb(196, 58, 48) 100%)",
      // background: `${gradientStyle}, hsl(var(--p))`,
      backgroundSize: "cover",
    };
  }
});
const followId = ref<number | null>(null);
const admireId = ref<number | null>(null);
const admireRequest = ref<Admirer | null>(null);
const followStore = useFollow();
const admireStore = useAdmire();
const admirerCount = ref(props.mate.admirerCount);
const followerCount = ref(props.mate.followerCount);
const isLoading = ref(false);
const isFollowBtnLoading = ref(false);
const isAdmireBtnLoading = ref(false);

const initData = () => {
  if (!props.enableBtnGroup) return;
  isLoading.value = true;
  const p1 = followStore.isFollowing(props.mate.id!).then((recordId) => {
    followId.value = recordId;
  });
  const p2 = admireStore.isAdmiring(props.mate.id!).then((admire) => {
    if (admire) {
      if (admire.status === 1) {
        admireRequest.value = admire;
      } else {
        admireId.value = admire.id!;
      }
    } else {
      admireId.value = null;
      admireRequest.value = null;
    }
  });
  Promise.all([p1, p2]).finally(() => {
    isLoading.value = false;
  });
};

watch(() => admireStore.reloadIdx, initData);

// Lifecycle
onMounted(initData);
// Event Handler
const onFollowBtnClick = async () => {
  try {
    isFollowBtnLoading.value = true;
    if (followId.value) {
      await followStore.unfollow(followId.value!);
      followId.value = null;
      followerCount.value = Math.max(0, followerCount.value - 1);
    } else {
      followId.value = (await followStore.follow(props.mate.id!)).id;
      followerCount.value++;
    }
  } catch (e) {
    showFailToast(i18n.global.t("Message.operationFailed"));
    throw e;
  } finally {
    isFollowBtnLoading.value = false;
  }
};
const onAdmireBtnClick = async () => {
  try {
    isAdmireBtnLoading.value = true;
    if (admireId.value) {
      await admireStore.cancelAdmire(admireId.value!);
      admireId.value = null;
      admirerCount.value = Math.max(0, admirerCount.value - 1);
      admireRequest.value = null;
    } else {
      admireRequest.value = await admireStore.admire(props.mate.id!);
      showSuccessToast(i18n.global.t("Message.applicationSentSuccessfully"));
    }
  } catch (e) {
    showFailToast(i18n.global.t("Message.operationFailed"));
    throw e;
  } finally {
    isAdmireBtnLoading.value = false;
  }
};
const onChatClick = async () => {
  await startChatSession(props.mate.id!, props.mate.uuid);
};
</script>
<template>
  <div
    class="rounded-2xl border-b w-full flex items-center justify-center gap-3 flex-col overflow-hidden relative"
  >
    <!-- 头像 & 名称 & 类型 -->
    <div
      class="flex justify-center items-center w-full px-5 pt-5 pb-2 relative aspect-[74/33]"
    >
      <div class="flex flex-col w-full gap-4">
        <div class="grid grid-cols-[auto_1fr] gap-4 w-full">
          <!-- 左：头像 -->
          <user-avatar :avatar-url="mate.mediaUrl" class="w-[5rem] relative" />
          <!-- 右：Mate信息 -->
          <div class="w-full flex flex-col gap-2 relative">
            <!-- 名称/性别/类型 -->
            <div class="flex items-center gap-2">
              <div class="flex gap-0.5 items-center">
                <div class="font-bold">
                  {{ mate.name }}
                </div>
                <gender-badge :gender="mate.gender" class="w-5" />
              </div>
              <div class="text-base-content/80">{{ mateType }}</div>
            </div>
            <!-- 关注/守护count -->
            <div class="flex gap-4">
              <div class="flex gap-1">
                {{ $t("Social.followers") }}
                <div class="font-bold">{{ followerCount }}</div>
              </div>
              <div class="flex gap-1">
                {{ $t("Social.admirer") }}
                <div class="font-bold">{{ admirerCount }}</div>
              </div>
            </div>
            <text-ellipsis
              :collapse-text="$t('Buttons.collapse')"
              :content="
                mate.bio ? mate.bio : i18n.global.t('Texts.noIntroductionText')
              "
              :expand-text="$t('Buttons.expand')"
              class="w-full text-base-content/80"
              rows="2"
              @click.stop
            />
          </div>
        </div>
        <div
          v-if="enableBtnGroup && mate.uuid !== mateStore.currentMate!.uuid"
          class="flex gap-2 justify-between w-full relative z-10"
        >
          <template v-if="!followId">
            <button
              :disabled="isLoading || isFollowBtnLoading"
              class="btn btn-xs btn-primary rounded-2xl flex-1"
              @click="onFollowBtnClick"
            >
              <span
                v-if="isLoading || isFollowBtnLoading"
                class="loading loading-xs"
              ></span>
              <follow-icon v-else class="h-4" />
              {{
                followId ? $t("User.mate.following") : $t("User.mate.follow")
              }}
            </button>
            <button
              v-if="showChatBtn"
              :disabled="isLoading"
              class="btn btn-xs rounded-2xl flex-1"
              @click="onChatClick"
            >
              <chat-icon class="h-4" />
              {{ $t("User.mate.chatString") }}
            </button>
          </template>
          <template v-else-if="followId && !admireId">
            <button
              :disabled="isLoading || isFollowBtnLoading"
              class="btn btn-xs rounded-2xl flex-1"
              @click="onFollowBtnClick"
            >
              <span
                v-if="isLoading || isFollowBtnLoading"
                class="loading loading-xs"
              ></span>
              <follow-icon v-else class="h-4" />
              {{
                followId ? $t("User.mate.following") : $t("User.mate.follow")
              }}
            </button>
            <button
              v-if="showChatBtn"
              :disabled="isLoading"
              class="btn btn-xs rounded-2xl flex-1"
              @click="onChatClick"
            >
              <chat-icon class="h-4" />
              {{ $t("User.mate.chatString") }}
            </button>
            <button
              :class="{ 'col-span-2': showChatBtn }"
              :disabled="
                isLoading ||
                isAdmireBtnLoading ||
                (!admireId && !!admireRequest)
              "
              class="btn-xs btn btn-primary rounded-2xl flex-1"
              @click="onAdmireBtnClick"
            >
              <span
                v-if="isLoading || isAdmireBtnLoading"
                class="loading loading-xs"
              ></span>
              <admire-icon v-else class="h-full" />
              {{
                admireId
                  ? $t("User.mate.guarding")
                  : admireRequest
                  ? $t("User.mate.guardianshipApplied")
                  : $t("User.mate.guard")
              }}
            </button>
          </template>
          <template v-else-if="admireId">
            <button
              :disabled="isLoading || isAdmireBtnLoading"
              class="btn-xs btn rounded-2xl flex-1"
              @click="onAdmireBtnClick"
            >
              <span
                v-if="isLoading || isAdmireBtnLoading"
                class="loading loading-xs"
              ></span>
              <admire-icon v-else class="h-full" />
              {{ admireId ? $t("User.mate.guarding") : $t("User.mate.guard") }}
            </button>
            <button
              v-if="showChatBtn"
              :disabled="isLoading"
              class="btn btn-xs rounded-2xl flex-1"
              @click="onChatClick"
            >
              <chat-icon class="h-4" />
              {{ $t("User.mate.chatString") }}
            </button>
            <button
              v-if="!mateStore.isMyMate(mate.uuid)"
              :class="{ 'col-span-2': showChatBtn }"
              class="btn-xs btn btn-primary rounded-2xl flex-1 line-clamp-1"
              @click="
                $router.push({
                  name: 'threadCommit',
                  params: { uuid: mate.uuid },
                })
              "
            >
              <request-icon class="icon-sm" />
              {{ $t("Buttons.createForTa") }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
